import { Row, buildActionColumn, buildIconTextColumn } from "@/components/Table";
import Tag from "@/components/Tag";
import { TagType } from "@/components/Tag/Tag";
import { toFormat, isInFuture } from "@/lib/dateHelpers";
import { StudentInternship } from "../../../internships/types";

export type InternshipRow = {
  id: string;
  facility: string;
  supervisor: string;
  supervisorWarning?: boolean;
  startDate: string;
  endDate: string;
  credits: string;
  status: "Completed" | "Not Started" | "In Progress";
  internship?: StudentInternship;
};

interface BuildInternshipColumnsProps {
  setMenu: (menu: any) => void;
  actionId: (rowId: string) => string;
  canDelete?: boolean;
  onEdit?: (internship: any) => void;
  onViewFacility?: (internship: any) => void;
  onDelete?: (row: InternshipRow) => void;
}

const futureDateWarningIcon = (supportingText: string) => ({
  name: "emergency_home" as const,
  size: 20,
  className: "text-extended-warning-brand-color",
  tooltip: {
    supportingText,
    variant: "plain" as const,
    color: "black" as const,
    menuCorner: "end-start" as const,
    anchorCorner: "start-start" as const
  }
});

export const buildInternshipColumns = ({
  setMenu,
  actionId,
  canDelete,
  onEdit,
  onViewFacility,
  onDelete
}: BuildInternshipColumnsProps) => {
  return [
    {
      accessorKey: "facility",
      header: "Facility",
      enableSorting: false,
      size: 350,
      minSize: 200
    },
    {
      accessorKey: "supervisor",
      header: "Supervisor",
      enableSorting: false,
      size: 350,
      minSize: 150
    },
    buildIconTextColumn<InternshipRow>({
      accessorKey: "startDate",
      header: "Start Date",
      enableSorting: false,
      getText: (row) => (row.startDate ? toFormat(row.startDate) : ""),
      getIcon: (row) =>
        row.startDate && isInFuture(row.startDate) ? futureDateWarningIcon("Start date is in the future") : null,
      size: 150,
      minSize: 100
    }),
    buildIconTextColumn<InternshipRow>({
      accessorKey: "endDate",
      header: "End Date",
      enableSorting: false,
      getText: (row) => (row.endDate ? toFormat(row.endDate) : ""),
      getIcon: (row) => {
        const isFutureEnd = row.endDate && isInFuture(row.endDate);
        const isFutureStart = row.startDate && isInFuture(row.startDate);
        return isFutureEnd || (!row.endDate && isFutureStart)
          ? futureDateWarningIcon("End date is in the future")
          : null;
      },
      size: 150,
      minSize: 100
    }),
    {
      accessorKey: "credits",
      header: "Months",
      cell: ({ row }: { row: Row<InternshipRow> }) => parseFloat(row.original.credits || "0").toFixed(1),
      enableSorting: false,
      size: 150,
      minSize: 80
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }: { row: Row<InternshipRow> }) => {
        const statusConfig: { [key: string]: { variant: TagType; text: string } } = {
          Completed: { variant: "success" as const, text: "Completed" },
          "Not Started": { variant: "surface" as const, text: "Not Started" },
          "In Progress": { variant: "warn" as const, text: "In Progress" }
        };

        const config = statusConfig[row.original.status];
        return (
          <Tag tagType={config.variant} size="small">
            {config.text}
          </Tag>
        );
      },
      enableSorting: false,
      meta: {
        headerClassName: "hide-header-in-column"
      },
      size: 120,
      minSize: 100
    },
    buildActionColumn<InternshipRow>({
      getActions: (row) => {
        const isActive = row.status === "In Progress";

        return [
          {
            text: "Edit Internship",
            onClick: () => onEdit?.(row.internship || row)
          },
          ...(canDelete && !isActive
            ? [
                {
                  text: "Delete Internship",
                  destructive: true as const,
                  iconName: "delete" as const,
                  onClick: () => onDelete?.(row)
                }
              ]
            : [])
        ];
      },
      onViewClick: (row) => onViewFacility?.(row),
      viewButtonTitle: "View Facility",
      actionId,
      setMenu
    })
  ];
};

export const getInternshipStatusColor = (status: string): TagType => {
  switch (status.toLowerCase()) {
    case "completed":
      return "success";
    case "not started":
      return "surface";
    case "in progress":
      return "warn";
    default:
      return "surface";
  }
};
